import type { BulkField } from '@/src/constants'

export type ParsedCsv = {
  headers: string[]
  rows: string[][]
}

/**
 * Catalog bulk map targets — Create Product + Add Stock only.
 * Inventory location is chosen in the modal (not mapped from CSV).
 * API computes stock from purchase_qty × pack_quantity (same as Add Stock POST).
 */
export const WORKSPACE_PRODUCT_BULK_MAP_FIELDS: BulkField[] = [
  // Create product
  { label: 'Product name', name: 'generic_name' },
  { label: 'Brand name', name: 'brand_name' },
  { label: 'Form / kind', name: 'dosage_form' },
  { label: 'Base UoM', name: 'inventory_unit' },
  { label: 'Purchase unit', name: 'packaging' },
  { label: 'Units per purchase unit', name: 'pack_quantity' },
  { label: 'Route', name: 'route' },
  { label: 'Strength', name: 'strength' },
  { label: 'Therapeutic category', name: 'therapeutic_category' },
  { label: 'Requires Rx', name: 'requires_rx' },
  { label: 'OTC', name: 'otc' },
  { label: 'Reorder level', name: 'reorder_level' },
  // Add stock
  { label: 'Batch number', name: 'batch_number' },
  { label: 'Purchase qty', name: 'purchase_qty' },
  { label: 'Expiry date', name: 'expiry_date' },
  { label: 'Manufacture date', name: 'date_of_manufacture' },
  { label: 'NAFDAC', name: 'nafdac_number' },
  { label: 'Unit cost', name: 'unit_cost' },
  { label: 'Selling price', name: 'selling_price' }
]

/** Must be mapped before Import (matches Create/Add required + backend needs). */
export const BULK_REQUIRED_MAP_FIELDS = [
  'generic_name',
  'dosage_form',
  'route',
  'inventory_unit',
  'pack_quantity',
  'purchase_qty',
  'strength',
  'batch_number'
] as const

/** Headers written to the file posted to bulk_import (Workspace Create + Add Stock). */
const CANONICAL_API_HEADERS = [
  'inventory_location',
  'inventory_location_id',
  'generic_name',
  'dosage_form',
  'route',
  'brand_name',
  'inventory_unit',
  'requires_rx',
  'otc',
  'therapeutic_category',
  'strength',
  'reorder_level',
  'batch_number',
  'expiry_date',
  'date_of_manufacture',
  'purchase_qty',
  'pack_quantity',
  'packaging',
  'nafdac_number',
  'unit_cost',
  'selling_price',
  'stock_location'
] as const

export type ColumnMap = Record<string, string>

/** Normalize header for fuzzy match: lowercase, spaces/underscores collapsed. */
export const normalizeHeaderKey = (s: string) =>
  s
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase()
    .replace(/[\s\-]+/g, '_')
    .replace(/_+/g, '_')

const parseCsvLine = (line: string): string[] => {
  const cells: string[] = []
  let cur = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        cur += ch
      }
      continue
    }
    if (ch === '"') {
      inQuotes = true
      continue
    }
    if (ch === ',') {
      cells.push(cur)
      cur = ''
      continue
    }
    cur += ch
  }
  cells.push(cur)
  return cells
}

export const parseCsv = (text: string): ParsedCsv => {
  const normalized = text
    .replace(/^\uFEFF/, '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
  const lines = normalized.split('\n').filter((l, idx, arr) => !(idx === arr.length - 1 && l === ''))
  if (!lines.length) return { headers: [], rows: [] }

  const headers = parseCsvLine(lines[0]).map(h => h.trim())
  const rows = lines
    .slice(1)
    .map(parseCsvLine)
    .filter(row => row.some(c => c.trim() !== ''))

  return { headers, rows }
}

export const escapeCsvCell = (value: string) => {
  if (/[",\n\r]/.test(value)) return `"${value.replace(/"/g, '""')}"`
  return value
}

export const autoMapHeaders = (csvHeaders: string[], targetFields: BulkField[]): ColumnMap => {
  const byNorm = new Map<string, string>()
  for (const h of csvHeaders) {
    const key = normalizeHeaderKey(h)
    if (key && !byNorm.has(key)) byNorm.set(key, h)
  }

  // Friendly aliases → target field name
  const aliases: Record<string, string> = {
    product_name: 'generic_name',
    name: 'generic_name',
    form: 'dosage_form',
    form_or_kind: 'dosage_form',
    dosage_form: 'dosage_form',
    base_uom: 'inventory_unit',
    base_unit: 'inventory_unit',
    inventory_unit: 'inventory_unit',
    purchase_unit: 'packaging',
    packaging: 'packaging',
    units_per_purchase_unit: 'pack_quantity',
    units_per_pack: 'pack_quantity',
    pack_quantity: 'pack_quantity',
    manufacture_date: 'date_of_manufacture',
    date_of_manufacture: 'date_of_manufacture',
    nafdac: 'nafdac_number',
    nafdac_number: 'nafdac_number',
    purchase_quantity: 'purchase_qty',
    purchase_qty: 'purchase_qty',
    quantity: 'purchase_qty',
    // Patient bulk upload
    firstname: 'first_name',
    first: 'first_name',
    middlename: 'middle_name',
    middle: 'middle_name',
    lastname: 'last_name',
    last: 'last_name',
    surname: 'last_name',
    sex: 'gender',
    dob: 'date_of_birth',
    birth_date: 'date_of_birth',
    birthdate: 'date_of_birth',
    date_of_birth: 'date_of_birth',
    phone: 'phone_number',
    mobile: 'phone_number',
    mobile_number: 'phone_number',
    telephone: 'phone_number',
    phone_number: 'phone_number',
    e_mail: 'email',
    mail: 'email',
    ref: 'ref',
    hospital_number: 'ref',
    hospital_no: 'ref',
    mrn: 'ref'
  }

  const map: ColumnMap = {}
  for (const field of targetFields) {
    const direct = byNorm.get(normalizeHeaderKey(field.name)) || byNorm.get(normalizeHeaderKey(field.label)) || ''
    if (direct) {
      map[field.name] = direct
      continue
    }
    // scan aliases
    let found = ''
    for (const [alias, target] of Object.entries(aliases)) {
      if (target !== field.name) continue
      const h = byNorm.get(alias)
      if (h) {
        found = h
        break
      }
    }
    map[field.name] = found
  }
  return map
}

const cellValue = (row: string[], headerIndex: Map<string, number>, columnMap: ColumnMap, fieldName: string) => {
  const srcHeader = columnMap[fieldName]
  if (!srcHeader) return ''
  const idx = headerIndex.get(srcHeader)
  if (idx === undefined) return ''
  return String(row[idx] ?? '').trim()
}

type BuildArgs = {
  headers: string[]
  rows: string[][]
  columnMap: ColumnMap
  inventoryLocationId: string
  inventoryLocationName: string
}

/** Build API CSV: inject location; send purchase_qty like Add Stock POST (API computes stock). */
export const buildCanonicalCsv = ({
  headers,
  rows,
  columnMap,
  inventoryLocationId,
  inventoryLocationName
}: BuildArgs): File => {
  const headerIndex = new Map(headers.map((h, i) => [h, i]))

  const lines = [
    CANONICAL_API_HEADERS.join(','),
    ...rows.map(row => {
      const get = (name: string) => cellValue(row, headerIndex, columnMap, name)

      const values: Record<string, string> = {
        inventory_location: inventoryLocationName,
        inventory_location_id: inventoryLocationId,
        generic_name: get('generic_name'),
        dosage_form: get('dosage_form'),
        route: get('route') || 'N/A',
        brand_name: get('brand_name'),
        inventory_unit: get('inventory_unit'),
        requires_rx: get('requires_rx') || 'false',
        otc: get('otc') || 'false',
        therapeutic_category: get('therapeutic_category'),
        strength: get('strength') || 'N/A',
        reorder_level: get('reorder_level'),
        batch_number: get('batch_number'),
        expiry_date: get('expiry_date'),
        date_of_manufacture: get('date_of_manufacture'),
        purchase_qty: get('purchase_qty'),
        pack_quantity: get('pack_quantity') || '1',
        packaging: get('packaging'),
        nafdac_number: get('nafdac_number'),
        unit_cost: get('unit_cost'),
        selling_price: get('selling_price'),
        stock_location: 'pharmacy'
      }

      return CANONICAL_API_HEADERS.map(h => escapeCsvCell(values[h] ?? '')).join(',')
    })
  ]

  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' })
  return new File([blob], 'product-bulk-mapped.csv', { type: 'text/csv' })
}

export const requiredMapsComplete = (columnMap: ColumnMap) =>
  BULK_REQUIRED_MAP_FIELDS.every(name => Boolean(columnMap[name]))
